import { Product, ProductTenor } from '@protos/product';
import { OrderBookTopMessage } from './orderBookTop';
import { OtcQuoteMessage } from './otcQuote';

export interface Ticker {
  symbol: string;
  product_symbol: string;
  mid: string;
  full_price?: string;
  accrued_price?: string;
  timestamp: string;
}

type TickerMeta = {
  symbol: string;
  tenor: ProductTenor;
  product: Product;
  productSymbolRoot: string;
};

export type ChartTicker = {
  close: number;
  high: number;
  low: number;
  open: number;
  symbol: string;
  timestamp: string;
};

export const tickerMeta = (
  ticker: Ticker | OtcQuoteMessage | OrderBookTopMessage,
  productMap: Record<string, Product>,
  tenorMap: Record<string, ProductTenor>
): TickerMeta | undefined => {
  const { symbol, product_symbol } = ticker;

  const tenorCode = (symbol as string).substring(product_symbol.length);
  const tenor = tenorMap[tenorCode];
  const product = productMap[product_symbol];

  if (!tenor || !product) return;

  let productSymbolRoot = product.symbol;

  if (product.product_type == 'calendar' && product.calendar_type !== 'spread') productSymbolRoot = product.underlying_symbol;
  else if (product.calendar_type === 'spread' && product.tenor_frequency === 'quarterly') {
    const tail = product.symbol.substring(product.underlying_symbol.length, product.symbol.length);
    const productName = product.underlying_symbol.substring(0, product.underlying_symbol.length - 1);
    productSymbolRoot = `${productName}${tail}`;
  }

  return { symbol: symbol as string, tenor, product, productSymbolRoot };
};

export const getAllRequestedContractSymbols = (
  allProducts: string[],
  allTenors: string[],
  productMap: Record<string, Product>,
  tenorMap: Record<string, ProductTenor>
): string[] => {
  const result = allProducts.reduce((acc, product) => {
    allTenors.forEach(tenor => {
      let contractSymbol = '';
      const detailedProduct = productMap[product];
      const detailedTenor = tenorMap[tenor];

      switch (detailedTenor.frequency) {
        case 'daily':
          contractSymbol = detailedProduct.calendar_type !== 'spread' ? `${detailedProduct.symbol}${detailedTenor.code}` : '';
          break;
        case 'weekly':
          contractSymbol = detailedProduct.calendar_type !== 'spread' ? `${detailedProduct.symbol}wk${detailedTenor.code}` : '';
          break;
        case 'monthly':
          contractSymbol =
            detailedProduct.calendar_type !== 'spread'
              ? `${detailedProduct.symbol}${detailedTenor.code}`
              : `${detailedProduct.symbol}${detailedTenor.spread_code}`;
          break;
        case 'quarterly':
          contractSymbol = detailedProduct.calendar_type !== 'spread' ? `${detailedProduct.symbol}q${detailedTenor.code}` : '';
          break;
        case 'yearly':
          contractSymbol = detailedProduct.calendar_type !== 'spread' ? `${detailedProduct.symbol}y${detailedTenor.code}` : '';
          break;
        default:
          contractSymbol = '';
          break;
      }

      if (contractSymbol) acc.push(contractSymbol);
    });

    return acc;
  }, [] as string[]);

  return result;
};
